//@flow
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {makeStyles, Typography, Grid, Tabs, Tab} from '@material-ui/core';
import TrainingOverlay from '../TrainingOverlay';
import {useChatBotTraining} from '../../hooks';
import type {StoreChatbot} from '../../../entities';
import {getChatbotService} from '../../promises';
import TrainingTabPDF from './TrainingTabPDF';
import TrainingTabText from './TrainingTabText';
import TrainingTabFaq from './TrainingTabFaq';
import TrainingTabStoreInfo from './TrainingTabStoreInfo';
import TrainingTabProducts from './TrainingTabProducts';
import TrainingHistory from './TrainingHistory';
import {colors} from '../../../../assets/styles/colors';

const useStyles = makeStyles(theme => ({
  sectionCard: {
    padding: '1rem',
    [theme.breakpoints.down('xs')]: {
      padding: '.5rem',
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    // overflow: 'hidden',
    position: 'relative',
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  tabLabel: {
    textTransform: 'none',
  },
  tabContent: {
    marginTop: '.5rem',
  },
}));

type Props = {
  chatBot: StoreChatbot,
  onSaveSuccess: () => void,
};

const MAX_POLL_TRIES = 12;

const TrainingSection = React.memo(function TrainingSection(props: Props) {
  const classes = useStyles();

  const [chatBot, setChatBot] = useState<StoreChatbot>(props.chatBot);
  const chatBotId = chatBot.id;

  const pollTries = useRef(0);

  const {
    chatBotTraining,
    loading: trainingLoading,
    refetchChatBotTraining,
    pagination,
    page,
    setPage,
  } = useChatBotTraining(chatBotId);

  const [tabValue, setTabValue] = useState('PDF');

  const isTraining = chatBot?.state === 'TRAINING';
  const trainFailed = chatBot?.state === 'FAILED_TRAINING';

  const fetchChatBot = useCallback(() => {
    getChatbotService(chatBotId).then(response => {
      setChatBot(response);
      if (response?.state === 'TRAINED') {
        refetchChatBotTraining();
      }
    });
  }, [chatBotId, refetchChatBotTraining]);

  useEffect(() => {
    if (isTraining || trainFailed) {
      const delayInterval = 5000 + pollTries.current * 500;

      const maxTries = trainFailed ? 2 : MAX_POLL_TRIES;

      const interval = setInterval(() => {
        if (pollTries.current >= maxTries) {
          clearInterval(interval);
          return;
        }
        pollTries.current += 1;
        fetchChatBot();
      }, delayInterval);

      return () => {
        clearInterval(interval);
      };
    }
  }, [chatBot, fetchChatBot, isTraining, trainFailed]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const pdfTrainings = chatBotTraining.filter(t => t.trainingType === 'PDF');
  const textTraining = chatBotTraining.find(t => t.trainingType === 'TEXT');
  const text = textTraining ? textTraining.metadata.values.TEXT : '';
  const messagesTraining = chatBotTraining.find(
    t => t.trainingType === 'TRAINED_MESSAGES'
  );
  const questionAnswers = messagesTraining
    ? messagesTraining.metadata.values.TRAINED_MESSAGES
    : [];

  const lastStoredTraining = chatBotTraining.find(
    training => training.trainingType === 'STORE_INFORMATION'
  );

  const lastStoredProductsTraining = chatBotTraining.find(
    training => training.trainingType === 'PRODUCTS'
  );

  if (trainingLoading) {
    return (
      <Grid item xs={12}>
        <section className={classes.sectionCard}>
          <TrainingOverlay
            visible={trainingLoading}
            message="Cargando datos de entrenamiento..."
          />
          <Typography className={classes.subtitle}>Entrena tu bot</Typography>
        </section>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <section className={classes.sectionCard}>
        <TrainingOverlay
          visible={isTraining}
          message="Entrenando tu bot, por favor espera..."
          submessage="Este proceso puede tardar de 15 minutos a 1 hora dependiendo del tamaño de los archivos"
        />
        <Typography className={classes.subtitle}>Entrena tu bot</Typography>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label="Documento (PDF)"
            value="PDF"
            className={classes.tabLabel}
          />
          <Tab label="Texto" value="TEXT" className={classes.tabLabel} />
          <Tab
            label="Q&A"
            value="TRAINED_MESSAGES"
            className={classes.tabLabel}
          />
          <Tab
            label="Información de la tienda"
            value="STORE_INFORMATION"
            className={classes.tabLabel}
          />
          <Tab
            label="Productos"
            value="PRODUCTS"
            className={classes.tabLabel}
          />
        </Tabs>

        <div className={classes.tabContent}>
          {tabValue === 'STORE_INFORMATION' && (
            <TrainingTabStoreInfo
              chatBot={chatBot}
              onSaveSuccess={fetchChatBot}
              lastStoredTraining={lastStoredTraining}
            />
          )}
          {tabValue === 'PRODUCTS' && (
            <TrainingTabProducts
              chatBot={chatBot}
              onSaveSuccess={fetchChatBot}
              lastStoredTraining={lastStoredProductsTraining}
            />
          )}
          {tabValue === 'PDF' && (
            <TrainingTabPDF
              chatBot={chatBot}
              onSaveSuccess={fetchChatBot}
              initialTrainDocuments={pdfTrainings}
            />
          )}
          {tabValue === 'TEXT' && (
            <TrainingTabText
              chatBot={chatBot}
              onSaveSuccess={fetchChatBot}
              initialText={text}
            />
          )}
          {tabValue === 'TRAINED_MESSAGES' && (
            <TrainingTabFaq
              chatBot={chatBot}
              onSaveSuccess={fetchChatBot}
              initialQuestionAnswers={questionAnswers}
            />
          )}
        </div>

        {/* Training History Section merged */}
        <TrainingHistory
          chatBot={chatBot}
          training={isTraining}
          chatBotTraining={chatBotTraining}
          pagination={pagination}
          page={page}
          setPage={setPage}
          refetchChatBotTraining={refetchChatBotTraining}
        />
      </section>
    </Grid>
  );
});

export default TrainingSection;
